import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Icon from "../../../../components/UI/Icon";
import { productMetadata } from "../../../../dictionaries/products";
import { reportKeyMap } from "../../../../dictionaries/reportKeyMap";
import isSubscribed from "../../../../helpers/isSubscribed";
import { track } from "../../../../helpers/track";
import useReportsDetailGet, { Product } from "../../../../services/useReportsDetailGet";
import { ReportSummaryState } from "../../../../store/reportSummary/types";
import { AppState } from "../../../../store/store";
import ProductsType from "../../../../types/report/Products";
import styles from "./ProductReport.module.scss";
import Products from "./Products";
import ProductsFree from "./ProductsFree";

export interface Props {
  userId: string;
  product: Product;
  reportSummaryState: ReportSummaryState;
  activated: boolean;
}

const ProductReport = ({ userId, product, reportSummaryState, activated }: Props) => {
  const userIsSubscribed = isSubscribed();
  const [open, setOpen] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const { icon, plural, singular } = productMetadata[product];
  const [products, setProducts] = useState<ProductsType>({});
  const [productsCount, setProductsCount] = useState<number | null>(null);
  const service = useReportsDetailGet({ product, dispatch: userIsSubscribed });

  useEffect(() => {
    if (service && service.status === "loaded") {
      const { payload } = service;
      setProducts(payload.products);
    }
  }, [service]);

  useEffect(() => {
    if (activated) {
      setOpen(true);
    }
  }, [activated]);

  useEffect(() => {
    if (reportSummaryState.status === "loaded") {
      const { payload } = reportSummaryState;
      const key = reportKeyMap[product];
      const productList = payload[key] || [];
      setOverdue(productList.some(({ overdue }) => overdue));
      if (!userIsSubscribed) {
        setProductsCount(productList.length);
        if (product === 'accounts' && !open) {
          setOpen(!open);
        }
      }
    }
  }, [reportSummaryState.status, product, userIsSubscribed]);

  const handleClick = () => {
    setOpen(!open);
    const properties = { date: +new Date, product };
    track({ userId, event: "VIEW_REPORT_PRODUCT", properties });
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.content}
        onClick={handleClick}
      >
        <div className={`${styles.shape} ${overdue ? styles.overdue : ""}`}>
          <Icon icon={icon} />
        </div>
        {!userIsSubscribed && product === 'accounts' ?
          (<div className={styles.textContainer}>
            <p className={styles.label}>
              {plural}
            </p>
            {productsCount !== null && productsCount > 0 && (
              <div className={styles.previewText}>
                <Icon icon="visibility" className={styles.previewIcon} />
                <span>
                  Vista previa gratuita - Producto {productsCount > 0 ? `1 de ${productsCount}` : '0'}
                </span>
              </div>
            )}
          </div>) :
          (<div className={styles.textContainer}>
            <p className={styles.label}>
              {plural}
            </p>
            {productsCount !== null && productsCount > 0 && (
              <div className={styles.previewText}>
                <Icon icon="visibility" className={styles.previewIcon} />
                <span>
                  Tienes {productsCount} {productsCount === 1 ? singular.toLowerCase() : plural.toLowerCase()}
                </span>
              </div>
            )}
          </div>)
        }
        <Icon
          icon={open ? "expand_less" : "expand_more"}
          className={styles.icon}
        />
      </div>
      {open && userIsSubscribed && <Products products={products} />}
      {open && !userIsSubscribed && <ProductsFree product={product} overdue={overdue} />}
    </div>
  )
}

const mapStateToProps = (state: AppState) => ({
  userId: state.security.userId,
  reportSummaryState: state.reportSummary,
});

export default connect(mapStateToProps)(ProductReport);